import produce from 'immer'
import isEqual from 'react-fast-compare'

import {
  errorPreventiveCacheRead,
  readShareListItemFromCache,
  removeFromPaginated,
} from '@sketch/modules-common'

import {
  CollectionForSelectFragment,
  CollectionPreviewsFragment,
  CollectionSharePreviewFragment,
  GetCollectionSharesDocument,
  GetCollectionSharesQuery,
  GetCollectionSharesQueryVariables,
  GetProjectCollectionsDocument,
  GetProjectCollectionsQuery,
  GetProjectCollectionsQueryVariables,
  GetProjectCollectionsForSelectDocument,
  GetProjectCollectionsForSelectQuery,
  GetProjectCollectionsForSelectQueryVariables,
  GetProjectSharesDocument,
  GetProjectSharesQuery,
  GetProjectSharesQueryVariables,
  GetShareCollectionDocument,
  GetShareCollectionQuery,
  GetShareCollectionQueryVariables,
  ShareListItemFragment,
  ApolloCache,
} from '@sketch/gql-types'
import { ClientOrCache, evictFieldIf } from '@sketch/graphql-cache'

type SearchParams = GetProjectCollectionsQueryVariables['search']

export const OPTIMISTIC_COLLECTION_ID = 'optimistic-identifier'

const PLACEHOLDER_FILE: CollectionSharePreviewFragment['file'] = {
  __typename: 'File',
  identifier: 'placeholder-file',
  thumbnails: [],
}

interface AddSharesToCollectionProps {
  cache: ClientOrCache
  projectIdentifier: string
  collection: CollectionForSelectFragment
  search: SearchParams
  sharesToAdd: ShareListItemFragment[]
  updateShareCount?: boolean
}

export const addSharesToCollection = ({
  cache,
  projectIdentifier,
  collection,
  sharesToAdd,
  search,
  updateShareCount = false,
}: AddSharesToCollectionProps) => {
  // Move the shares to the given collection
  updateCollectionShares({
    cache,
    projectIdentifier,
    collectionIdentifier: collection.identifier,
    recipe: ({ project }) => {
      const { collection } = project
      const { shares } = collection
      // Only add shares that are not in the collection yet
      const newShares = sharesToAdd.filter(
        share => !shares.entries.find(s => s.identifier === share.identifier)
      )
      shares.entries.push(...newShares)
      shares.entries?.sort(sortByCreatedDesc)
      shares.meta.totalCount += newShares.length
    },
  })

  addSharesToCollectionPreviews({
    cache,
    projectIdentifier,
    collectionIdentifier: collection.identifier,
    search,
    sharesToAdd,
    updateShareCount,
  })

  // update the collection for each cached share
  for (const share of sharesToAdd) {
    updateShareCollection(cache, share.identifier, collection)
  }
}

interface RemoveSharesFromCollectionProps {
  cache: ClientOrCache
  projectIdentifier: string
  collectionIdentifier: string
  search: SearchParams
  sharesToRemove: Pick<ShareListItemFragment, 'identifier'>[]
  updateShareCount?: boolean
}

export const removeSharesFromCollection = ({
  cache,
  projectIdentifier,
  collectionIdentifier,
  search,
  sharesToRemove,
  updateShareCount = true,
}: RemoveSharesFromCollectionProps) => {
  const identifiers = new Set(sharesToRemove.map(s => s.identifier))
  // Remove the shares from the given collection
  for (const share of sharesToRemove) {
    removeFromPaginated(
      cache,
      { __typename: 'Share', identifier: share.identifier },
      key => key.includes(collectionIdentifier) && key.includes('.shares')
    )
  }

  // Remove the shares from the previews of the collection
  removeSharesFromCollectionPreviews({
    cache,
    projectIdentifier,
    collectionIdentifier,
    search,
    shareIdentifiers: identifiers,
    updateShareCount,
  })

  // Set share.collection to null for each share
  for (const identifier of identifiers) {
    updateShareCollection(cache, identifier, null)
  }
}

interface InvalidateCollectionSharesProps {
  cache: ApolloCache<any>
  collectionIdentifier: string
}

export const invalidateCollectionShares = ({
  cache,
  collectionIdentifier,
}: InvalidateCollectionSharesProps) => {
  type Field = keyof GetCollectionSharesQuery['project']['collection']
  const fieldName: Field = 'shares'

  const id = cache.identify({
    __typename: 'Collection',
    identifier: collectionIdentifier,
  })

  cache.evict({ id, fieldName })
  cache.gc()
}

export const invalidateShareAfterCollectionUpdate = ({
  cache,
  shareIdentifier,
}: {
  cache: ApolloCache<any>
  shareIdentifier: string
}) => {
  cache.evict({
    id: cache.identify({ __typename: 'Share', identifier: shareIdentifier }),
  })
  cache.gc()
}

interface InvalidateProjectSharesProps {
  cache: ApolloCache<any>
  projectIdentifier: string
}

export const invalidateProjectShares = ({
  cache,
  projectIdentifier,
}: InvalidateProjectSharesProps) => {
  const id = cache.identify({
    __typename: 'Project',
    identifier: projectIdentifier,
  })!

  type Fields = keyof GetProjectSharesQuery['project']
  const fieldName: Fields = 'shares'

  cache.evict({ fieldName, id })
  cache.gc()
}

interface InvalidateProjectCollectionsProps {
  cache: ApolloCache<any>
  projectIdentifier: string
}

export const invalidateProjectCollections = ({
  cache,
  projectIdentifier,
}: InvalidateProjectCollectionsProps) => {
  const id = cache.identify({
    __typename: 'Project',
    identifier: projectIdentifier,
  })!

  type Fields = keyof GetProjectCollectionsQuery['project']
  const fieldName: Fields = 'collections'

  cache.evict({ fieldName, id })
  cache.gc()
}

interface InvalidateCollectionsExceptProps {
  cache: ApolloCache<any>
  projectIdentifier: string
  search: SearchParams
}

/*
 * Invalidate all cached `project.collections` for the given project **except**
 * for the given search criteria.
 * When the user changes the list of collections (e.g. by creating a new
 * collection) we manually update `project.collections(search: ...)` with the
 * current search criteria and then use this funciton to invalidate any other
 * cached `project.collections`.
 * This ensures that the UI is up to date, and that if the user changes their
 * search criteria `project.collections` will be re-fetched.
 */
export const invalidateCollectionsExcept = ({
  cache,
  projectIdentifier,
  search,
}: InvalidateCollectionsExceptProps) => {
  const searchTerm = search?.name || ''
  const filters = search?.filters || []

  type CollectionsFieldArgs = {
    shareSearch?: GetProjectCollectionsQueryVariables['search']
    after?: GetProjectCollectionsQueryVariables['after']
  }

  const id = cache.identify({
    __typename: 'Project',
    identifier: projectIdentifier,
  })

  type Fields = keyof GetProjectCollectionsQuery['project']
  const fieldName: Fields = 'collections'

  evictFieldIf<CollectionsFieldArgs>(
    cache,
    { id, fieldName },
    fieldArguments => {
      const fieldSearchTerm = fieldArguments?.shareSearch?.name ?? ''
      const fieldFilters = fieldArguments?.shareSearch?.filters ?? []

      return fieldSearchTerm !== searchTerm || !isEqual(fieldFilters, filters)
    }
  )
  cache.gc()
}

interface UpdateCollectionMetadataProps {
  cache: ClientOrCache
  projectIdentifier: string
  collection: Pick<
    CollectionPreviewsFragment,
    'identifier' | 'name' | 'description'
  >
}

export const updateCollectionMetadata = ({
  cache,
  projectIdentifier,
  collection: { identifier, name, description },
}: UpdateCollectionMetadataProps) => {
  updateCollectionShares({
    cache,
    projectIdentifier,
    collectionIdentifier: identifier,
    recipe: cachedValue => {
      const collectionToUpdate = cachedValue.project.collection
      collectionToUpdate.name = name
      collectionToUpdate.description = description
    },
  })

  updateProjectCollectionsForSelect(cache, projectIdentifier, ({ project }) => {
    const collectionToUpdate = project.collectionsUnpaginated.find(
      c => c.identifier === identifier
    )
    if (!collectionToUpdate) {
      return
    }
    collectionToUpdate.name = name
  })

  sortProjectCollections({ cache, projectIdentifier })
}

interface AddCollectionToProjectProps {
  cache: ClientOrCache
  projectIdentifier: string
  collection: CollectionPreviewsFragment
  search?: SearchParams
}

export const addCollectionToProject = (props: AddCollectionToProjectProps) => {
  addCollectionToProjectCollections(props)
  addCollectionToProjectCollectionsForSelect(props)
}

interface RemoveCollectionFromProjectProps {
  cache: ClientOrCache
  projectIdentifier: string
  collectionIdentifier: string
}

export const removeCollectionFromProject = (
  props: RemoveCollectionFromProjectProps
) => {
  const shares = getCollectionShares(props)

  // Remove the reference to the collection from the shares
  for (const share of shares) {
    updateShareCollection(props.cache, share.identifier, null)
  }

  // Remove the collection from the project
  removeCollectionFromProjectCollections(props)
  removeCollectionFromProjectCollectionsForSelect(props)
}

interface SortProjectCollectionsProps {
  cache: ClientOrCache
  projectIdentifier: string
}

export function sortProjectCollections({
  cache,
  projectIdentifier,
}: SortProjectCollectionsProps) {
  updateProjectCollections({
    cache,
    projectIdentifier,
    recipe: ({ project }) => {
      const { collections } = project
      collections.entries.sort(sortByNameAsc)
    },
  })
  updateProjectCollectionsForSelect(cache, projectIdentifier, ({ project }) => {
    const { collectionsUnpaginated } = project
    collectionsUnpaginated.sort(sortByNameAsc)
  })
}

interface UpdateCollectionSharesProps {
  cache: ClientOrCache
  projectIdentifier: string
  collectionIdentifier: string
  recipe: (cachedObject: GetCollectionSharesQuery) => void
}

function updateCollectionShares({
  cache,
  projectIdentifier,
  collectionIdentifier,
  recipe,
}: UpdateCollectionSharesProps) {
  const cacheArgs = {
    query: GetCollectionSharesDocument,
    variables: {
      projectIdentifier,
      collectionIdentifier,
      after: null,
      search: { name: '', filters: [] },
      sortOrder: 'LAST_MODIFIED_DESC' as const,
    },
  }
  const cachedCollection = errorPreventiveCacheRead<
    GetCollectionSharesQuery,
    GetCollectionSharesQueryVariables
  >(cache, cacheArgs)

  if (!cachedCollection) {
    return
  }

  const collection = produce(cachedCollection, recipe)

  cache.writeQuery<GetCollectionSharesQuery, GetCollectionSharesQueryVariables>(
    { ...cacheArgs, data: collection }
  )

  return collection
}

interface GetCollectionSharesProps {
  cache: ClientOrCache
  projectIdentifier: string
  collectionIdentifier: string
}

function getCollectionShares({
  cache,
  projectIdentifier,
  collectionIdentifier,
}: GetCollectionSharesProps) {
  const cacheArgs = {
    query: GetCollectionSharesDocument,
    variables: {
      projectIdentifier,
      collectionIdentifier,
      after: null,
      search: { name: '', filters: [] },
    },
  }
  const cachedValue = errorPreventiveCacheRead<
    GetCollectionSharesQuery,
    GetCollectionSharesQueryVariables
  >(cache, cacheArgs)

  return cachedValue?.project.collection.shares.entries ?? []
}

interface AddSharesToProjectProps {
  cache: ClientOrCache
  projectIdentifier: string
  sharesToAdd: ShareListItemFragment[]
}

export const addSharesToProject = ({
  cache,
  projectIdentifier,
  sharesToAdd,
}: AddSharesToProjectProps) => {
  updateProjectShares({
    cache,
    projectIdentifier,
    recipe: ({ project }) => {
      const { shares } = project
      // Only add shares that are not in the project yet
      const newShares = sharesToAdd
        .filter(
          share => !shares.entries.find(s => s.identifier === share.identifier)
        )
        // no need to have a collection defined
        .map(share => ({ ...share, collection: null }))
      shares.entries.push(...newShares)
      shares.entries.sort(sortByCreatedDesc)
      shares.meta.totalCount += newShares.length
    },
  })
}

export const removeSharesFromProject = (
  cache: ClientOrCache,
  projectIdentifier: string,
  sharesToRemove: Pick<ShareListItemFragment, 'identifier'>[]
) => {
  for (const share of sharesToRemove) {
    removeFromPaginated(
      cache,
      { __typename: 'Share', identifier: share.identifier },
      key =>
        key.includes(projectIdentifier) &&
        key.includes('.shares') &&
        key.includes('NO_COLLECTION')
    )
  }
}

interface UpdateProjectSharesProps {
  cache: ClientOrCache
  projectIdentifier: string
  recipe: (cachedObject: GetProjectSharesQuery) => void
}

function updateProjectShares({
  cache,
  projectIdentifier,
  recipe,
}: UpdateProjectSharesProps) {
  const cacheArgs = {
    query: GetProjectSharesDocument,
    variables: {
      shortId: projectIdentifier,
      after: null,
      search: {
        name: null,
        isCurrentVersionDownloadable: null,
        filters: ['NO_COLLECTION' as const],
      },
      sortOrder: 'LAST_MODIFIED_DESC' as const,
    },
  }
  const cachedProject = errorPreventiveCacheRead<
    GetProjectSharesQuery,
    GetProjectSharesQueryVariables
  >(cache, cacheArgs)

  if (!cachedProject) {
    return
  }

  const project = produce(cachedProject, recipe)

  cache.writeQuery<GetProjectSharesQuery, GetProjectSharesQueryVariables>({
    ...cacheArgs,
    data: project,
  })

  return project
}

function sortByCreatedDesc(
  current: ShareListItemFragment,
  next: ShareListItemFragment
) {
  return (
    Date.parse(next.version?.createdAt as string) -
    Date.parse(current.version?.createdAt as string)
  )
}

function sortByNameAsc(current: { name: string }, next: { name: string }) {
  return current.name.localeCompare(next.name, undefined, {
    numeric: true,
    sensitivity: 'base',
  })
}

export function updateShareCollection(
  cache: ClientOrCache,
  shareIdentifier: string,
  collection: CollectionForSelectFragment | null
) {
  const cacheArgs = {
    query: GetShareCollectionDocument,
    variables: { shareIdentifier },
  }
  const cachedValue = errorPreventiveCacheRead<
    GetShareCollectionQuery,
    GetShareCollectionQueryVariables
  >(cache, cacheArgs)

  if (!cachedValue) {
    return
  }

  const updatedShare = produce(cachedValue, ({ share }) => {
    if (!share) {
      return
    }

    share.collection = collection
  })

  cache.writeQuery<GetShareCollectionQuery, GetShareCollectionQueryVariables>({
    ...cacheArgs,
    data: updatedShare,
  })
}

interface AddSharesToCollectionPreviewsProps {
  cache: ClientOrCache
  projectIdentifier: string
  collectionIdentifier: string
  search: SearchParams
  sharesToAdd: ShareListItemFragment[]
  updateShareCount?: boolean
}

function addSharesToCollectionPreviews({
  cache,
  projectIdentifier,
  collectionIdentifier,
  search,
  sharesToAdd,
  updateShareCount = false,
}: AddSharesToCollectionPreviewsProps) {
  updateCollectionPreviews({
    cache,
    projectIdentifier,
    collectionIdentifier,
    search,
    recipe: collection => {
      const newShares = sharesToAdd.filter(
        share =>
          !collection.previews.find(p => p.shareIdentifier === share.identifier)
      )

      const previewsToAdd = newShares.map(share => ({
        __typename: 'CollectionSharePreview' as const,
        shareIdentifier: share.identifier,
        name: share.name,
        file: share.version?.document?.previewFile ?? null,
      }))

      collection.previews = collection.previews.concat(previewsToAdd)
      if (updateShareCount) {
        collection.shareCount += previewsToAdd.length
      }
    },
  })
}

interface RemoveSharesFromCollectionPreviewsProps {
  cache: ClientOrCache
  shareIdentifiers: Set<string>
  projectIdentifier: string
  collectionIdentifier: string
  search: SearchParams
  updateShareCount?: boolean
}
function removeSharesFromCollectionPreviews({
  cache,
  shareIdentifiers,
  projectIdentifier,
  collectionIdentifier,
  search,
  updateShareCount = true,
}: RemoveSharesFromCollectionPreviewsProps) {
  updateCollectionPreviews({
    cache,
    projectIdentifier,
    collectionIdentifier,
    search,
    recipe: collection => {
      const initialCount = collection.previews.length
      collection.previews = collection.previews.filter(
        p => !shareIdentifiers.has(p.shareIdentifier)
      )
      if (updateShareCount) {
        const numRemoved = initialCount - collection.previews.length
        collection.shareCount -= numRemoved
      }
    },
  })
}

interface UpdateCollectionPreviewsProps {
  cache: ClientOrCache
  projectIdentifier: string
  collectionIdentifier: string
  search?: GetProjectCollectionsQueryVariables['search']
  recipe: (cachedObject: CollectionPreviewsFragment) => void
}

function updateCollectionPreviews({
  cache,
  projectIdentifier,
  collectionIdentifier,
  search,
  recipe,
}: UpdateCollectionPreviewsProps) {
  const cachedValue = errorPreventiveCacheRead<
    GetProjectCollectionsQuery,
    GetProjectCollectionsQueryVariables
  >(cache, {
    query: GetProjectCollectionsDocument,
    variables: { projectIdentifier, search, after: null },
  })

  if (!cachedValue) {
    return
  }

  const updatedProject = produce(cachedValue, ({ project }) => {
    const { collections } = project
    const collection = collections.entries.find(
      c => c.identifier === collectionIdentifier
    )

    if (!collection) {
      return
    }

    recipe(collection)
  })

  cache.writeQuery<
    GetProjectCollectionsQuery,
    GetProjectCollectionsQueryVariables
  >({
    query: GetProjectCollectionsDocument,
    variables: { projectIdentifier, after: null },
    data: updatedProject,
  })
}

function addCollectionToProjectCollections({
  cache,
  projectIdentifier,
  search,
  collection,
}: AddCollectionToProjectProps) {
  updateProjectCollections({
    cache,
    projectIdentifier,
    search,
    recipe: ({ project }) => {
      const { collections } = project

      // Skip adding the collection if it is already in the project
      const existingCollection = collections.entries.find(
        c => c.identifier === collection.identifier
      )
      if (existingCollection) {
        return
      }

      collections.entries.push(collection)
      collections.entries.sort(sortByNameAsc)
      collections.meta.totalCount++
    },
  })
}

export function removeCollectionFromProjectCollections({
  cache,
  projectIdentifier,
  collectionIdentifier,
}: RemoveCollectionFromProjectProps) {
  removeFromPaginated(
    cache,
    { __typename: 'Collection', identifier: collectionIdentifier },
    key => key.includes(projectIdentifier) && key.includes('.collections')
  )
}

interface UpdateProjectCollectionsProps {
  cache: ClientOrCache
  projectIdentifier: string
  search?: SearchParams
  recipe: (cachedObject: GetProjectCollectionsQuery) => void
}

function updateProjectCollections({
  cache,
  projectIdentifier,
  search,
  recipe,
}: UpdateProjectCollectionsProps) {
  const cacheArgs = {
    query: GetProjectCollectionsDocument,
    variables: { projectIdentifier, search, after: null },
  }
  const cachedValue = errorPreventiveCacheRead<
    GetProjectCollectionsQuery,
    GetProjectCollectionsQueryVariables
  >(cache, cacheArgs)

  if (!cachedValue) {
    return
  }

  const updated = produce(cachedValue, recipe)

  cache.writeQuery<
    GetProjectCollectionsQuery,
    GetProjectCollectionsQueryVariables
  >({ ...cacheArgs, data: updated })
}

function addCollectionToProjectCollectionsForSelect({
  cache,
  projectIdentifier,
  collection,
}: AddCollectionToProjectProps) {
  const unpaginatedCollection = {
    __typename: 'UnpaginatedCollection' as const,
    identifier: collection.identifier,
    name: collection.name,
  }

  updateProjectCollectionsForSelect(cache, projectIdentifier, ({ project }) => {
    const { collectionsUnpaginated } = project

    // Skip adding the collection if it is already in the project
    const existingCollection = collectionsUnpaginated.find(
      c => c.identifier === collection.identifier
    )
    if (existingCollection) {
      return
    }

    collectionsUnpaginated.push(unpaginatedCollection)
    collectionsUnpaginated.sort(sortByNameAsc)
  })
}

export function removeCollectionFromProjectCollectionsForSelect({
  cache,
  projectIdentifier,
  collectionIdentifier,
}: RemoveCollectionFromProjectProps) {
  updateProjectCollectionsForSelect(cache, projectIdentifier, ({ project }) => {
    project.collectionsUnpaginated = project.collectionsUnpaginated.filter(
      collection => collection.identifier !== collectionIdentifier
    )
  })
}

function updateProjectCollectionsForSelect(
  cache: ClientOrCache,
  projectIdentifier: string,
  recipe: (cachedObject: GetProjectCollectionsForSelectQuery) => void
) {
  const cacheArgs = {
    query: GetProjectCollectionsForSelectDocument,
    variables: { projectIdentifier },
  }
  const cachedValue = errorPreventiveCacheRead<
    GetProjectCollectionsForSelectQuery,
    GetProjectCollectionsForSelectQueryVariables
  >(cache, cacheArgs)

  if (!cachedValue) {
    return
  }

  const updated = produce(cachedValue, recipe)

  cache.writeQuery<
    GetProjectCollectionsForSelectQuery,
    GetProjectCollectionsForSelectQueryVariables
  >({ ...cacheArgs, data: updated })
}

interface CreateOptimisticCollectionProps {
  cache: ClientOrCache
  shareIds: string[]
}
export const createOptimisticCollection = ({
  cache,
  shareIds,
}: CreateOptimisticCollectionProps) => {
  const previews: CollectionSharePreviewFragment[] = []

  shareIds.forEach(identifier => {
    const share = readShareListItemFromCache({
      cache,
      id: identifier,
    })

    if (!share) {
      return
    }

    previews.push({
      __typename: 'CollectionSharePreview',
      shareIdentifier: share.identifier,
      name: share.name,
      file: share.version?.document?.previewFile ?? PLACEHOLDER_FILE,
    })
  })

  const optimisticCollection = {
    identifier: OPTIMISTIC_COLLECTION_ID,
    name: '',
    description: '',
    previews,
    shareCount: shareIds.length,
  }

  return optimisticCollection
}

interface TransferAllSharesProps {
  cache: ClientOrCache
  originIdentifier: string
  targetIdentifier: string
  projectIdentifier: string
}

export const transferAllShares = ({
  cache,
  projectIdentifier,
  originIdentifier,
  targetIdentifier,
}: TransferAllSharesProps) => {
  const cachedValue = errorPreventiveCacheRead<
    GetProjectCollectionsQuery,
    GetProjectCollectionsQueryVariables
  >(cache, {
    query: GetProjectCollectionsDocument,
    variables: { projectIdentifier, after: null },
  })

  if (!cachedValue) {
    return
  }

  const updatedProject = produce(cachedValue, ({ project }) => {
    const { collections } = project
    const originCollection = collections.entries.find(
      c => c.identifier === originIdentifier
    )

    if (!originCollection) {
      return
    }

    const targetCollection = collections.entries.find(
      c => c.identifier === targetIdentifier
    )

    if (targetCollection) {
      targetCollection.shareCount += originCollection.shareCount
      targetCollection.previews = targetCollection.previews.concat(
        originCollection.previews
      )
    }

    originCollection.previews = []
    originCollection.shareCount = 0
  })

  cache.writeQuery<
    GetProjectCollectionsQuery,
    GetProjectCollectionsQueryVariables
  >({
    query: GetProjectCollectionsDocument,
    variables: { projectIdentifier, after: null },
    data: updatedProject,
  })
}
